import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { RotateCcw, Trash2 } from "lucide-react";
import { DOC_TYPES, StatusPill, inr } from "./shared";

/**
 * TrashTab — Feb 2026
 *
 * Lists soft-deleted drafts within the 30-day retention window.
 * Restore puts a document back into the main list with its original
 * `doc_no` (which was never recycled). After 30 days a background
 * purge removes the row entirely; this tab is the only way to recover
 * before then.
 */
const DOC_TYPE_LABELS = Object.fromEntries(
  DOC_TYPES.filter((t) => !t.isView).map((t) => [t.key, t.label]),
);

const daysLeft = (deletedAtIso) => {
  if (!deletedAtIso) return 0;
  const deleted = new Date(deletedAtIso).getTime();
  const cutoff = deleted + 30 * 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((cutoff - Date.now()) / (24 * 60 * 60 * 1000)));
};

export default function TrashTab({ ws, onReload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/gst/workspaces/${ws}/documents-trash`,
                                { params: typeFilter ? { doc_type: typeFilter } : {} });
      setRows(r.data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-line */ }, [ws, typeFilter]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const hay = [r.doc_no, r.customer_name, r.customer_gstin].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [rows, q]);

  const restore = async (r) => {
    if (!window.confirm(
      `Restore ${(r.doc_type || "document").replace(/_/g, " ")} ${r.doc_no}?\n\n`
      + `It will reappear in the main list with its original number.`
    )) return;
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${r.id}/restore`);
      toast.success(`${r.doc_no} restored`);
      await load();
      onReload?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-3" data-testid="gst-trash-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5">
          <Trash2 className="w-3.5 h-3.5" />
          Deleted drafts are kept for 30 days. After that they're purged permanently.
        </div>
        <select value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="h-9 border border-border bg-background rounded-none px-2 text-xs"
                data-testid="gst-trash-type-filter">
          <option value="">All document types</option>
          {Object.entries(DOC_TYPE_LABELS).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search doc no, customer…"
                className="h-9 w-64 rounded-none text-xs"
                data-testid="gst-trash-search" />
        <Button variant="outline" size="sm" className="h-9 rounded-none ml-auto"
                 onClick={load} disabled={loading}
                 data-testid="gst-trash-refresh">
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border"
             data-testid="gst-trash-empty">
          {loading ? "Loading…" : "Nothing in Trash. Deleted drafts show up here for 30 days."}
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs" data-testid="gst-trash-table">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Doc No</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                <th className="text-left px-3 py-2 font-semibold">Date</th>
                <th className="text-left px-3 py-2 font-semibold">Customer</th>
                <th className="text-right px-3 py-2 font-semibold">Grand Total</th>
                <th className="text-left px-3 py-2 font-semibold">Deleted At</th>
                <th className="text-left px-3 py-2 font-semibold">Expires in</th>
                <th className="text-right px-3 py-2 font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const grand = Number(r.totals?.grand_total || 0);
                const left = daysLeft(r.deleted_at);
                return (
                  <tr key={r.id} className="border-t border-border/40 hover:bg-muted/20"
                      data-testid={`gst-trash-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.doc_no || "—"}</td>
                    <td className="px-3 py-2">
                      <StatusPill status="draft" />
                      <span className="ml-2 text-muted-foreground">{DOC_TYPE_LABELS[r.doc_type] || r.doc_type}</span>
                    </td>
                    <td className="px-3 py-2">{r.doc_date || "—"}</td>
                    <td className="px-3 py-2">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">₹{inr(grand)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.deleted_at ? r.deleted_at.slice(0, 10) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`font-mono text-[11px] ${
                        left <= 3 ? "text-rose-600 font-semibold"
                                   : left <= 7 ? "text-amber-700" : "text-muted-foreground"
                      }`}>
                        {left} day{left === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs rounded-none text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                               onClick={() => restore(r)}
                               data-testid={`gst-trash-restore-${r.id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Restore
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
